import { AppConfig } from '@core/domain/AppConfig.ts';
import {
  encodeSqliteIdbBytes,
  putSqliteExportIfChanged,
  type SqliteIdbStoredCache,
} from '@src/modules/platform/local-sqlite/infrastructure/sqliteIdbCodec.ts';
import type { LocalSqlitePersistState } from '@src/modules/platform/local-sqlite/infrastructure/localSqlitePersistDirty.ts';
import { putLocalSqliteIdbBytesAtKey } from '@src/modules/platform/local-sqlite/infrastructure/sqliteIdbStorage.ts';
import type {
  SqliteIdbPersistWorkerIn,
  SqliteIdbPersistWorkerOut,
} from '@src/modules/platform/local-sqlite/infrastructure/sqliteIdbPersistWorker.ts';

type PendingPersist = {
  cache: SqliteIdbStoredCache;
  persistState?: LocalSqlitePersistState;
  resolve: (wrote: boolean) => void;
  reject: (error: Error) => void;
};

let worker: Worker | null | undefined;
let pending: PendingPersist | null = null;
let workerTail: Promise<unknown> = Promise.resolve();

async function persistSqliteExportSync(
  raw: Uint8Array,
  cache: SqliteIdbStoredCache,
  persistState?: LocalSqlitePersistState,
): Promise<boolean> {
  const key = AppConfig.getLocalSqliteIdbSqliteKey();
  if (cache && persistState) {
    const wrote = await putSqliteExportIfChanged(
      (stored) => putLocalSqliteIdbBytesAtKey(key, stored),
      raw,
      cache,
    );
    persistState.dirty = false;
    return wrote;
  }
  if (cache) {
    return putSqliteExportIfChanged((stored) => putLocalSqliteIdbBytesAtKey(key, stored), raw, cache);
  }
  await putLocalSqliteIdbBytesAtKey(key, await encodeSqliteIdbBytes(raw));
  return true;
}

function finishPending(out: SqliteIdbPersistWorkerOut): void {
  const job = pending;
  pending = null;
  if (!job) return;
  if (!out.ok) {
    job.reject(new Error(out.error));
    return;
  }
  if (out.cacheBytes) job.cache.bytes = out.cacheBytes;
  if (job.persistState) job.persistState.dirty = false;
  job.resolve(out.wrote);
}

function getPersistWorker(): Worker | null {
  if (worker === null) return null;
  if (worker != null) return worker;
  if (typeof Worker === 'undefined') {
    worker = null;
    return null;
  }
  try {
    const w = new Worker(new URL('./sqliteIdbPersistWorker.ts', import.meta.url), { type: 'module' });
    w.onmessage = (ev: MessageEvent<SqliteIdbPersistWorkerOut>) => finishPending(ev.data);
    w.onerror = () => {
      worker = null;
      if (pending) finishPending({ ok: false, error: 'sqlite persist worker failed' });
    };
    worker = w;
    return w;
  } catch {
    worker = null;
    return null;
  }
}

/** Gzip + IndexedDB write; worker when available, else main thread. */
export async function persistSqliteExportToIdb(
  raw: Uint8Array,
  cache?: SqliteIdbStoredCache,
  persistState?: LocalSqlitePersistState,
): Promise<boolean> {
  if (!cache) {
    return persistSqliteExportSync(raw, {}, persistState);
  }
  const w = getPersistWorker();
  if (!w) {
    return persistSqliteExportSync(raw, cache, persistState);
  }
  const run = (): Promise<boolean> =>
    new Promise((resolve, reject) => {
      pending = { cache, persistState, resolve, reject };
      const msg: SqliteIdbPersistWorkerIn = { raw, cacheBytes: cache.bytes };
      w.postMessage(msg, [raw.buffer]);
    });
  const p = workerTail.then(run, run);
  workerTail = p.then(
    () => undefined,
    () => undefined,
  );
  return p;
}
